import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import {
  AppWindow,
  ChevronRight,
  Copy,
  Download,
  ExternalLink,
  FileArchive,
  FileCode,
  FilePlus,
  Folder,
  FolderInput,
  FolderPlus,
  FolderUp,
  MoreHorizontal,
  Pencil,
  Plus,
  Settings2,
  Trash2,
  Upload,
  Users,
  X,
} from 'lucide-react';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type DragEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ApiError, api, requestWithMessage, uploadToSite } from '../lib/api';
import { formatBytes, formatDateTime, relativeTime } from '../lib/format';
import { useI18n, type TranslationKey } from '../lib/i18n';
import {
  SITES_KEY,
  cleanPath,
  droppedFiles,
  extensionOf,
  fileAddress,
  isMedia,
  listing,
  pickedFiles,
  pickedFolder,
  saveDownload,
  skipReason,
  zipFiles,
  type DownloadTarget,
  type Outgoing,
  type SiteDetails,
  type SiteDownload,
  type SiteFile,
  type SiteFolderRow,
  type SitesList,
  type SiteSummary,
  type SkipReason,
} from '../lib/sites';
import { ZipError, type ZipProblem } from '../lib/zip';
import { iconFor, useFilePolicy } from '../components/Attachments';
import {
  Badge,
  Button,
  Card,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  LoadingBlock,
  PageHeader,
  ProgressBar,
  Table,
  Td,
  Th,
  Tr,
  useToast,
} from '../components/ui';

/**
 * Sites (operator, 2026-11-03: "add sites feature this feature let employees who
 * has right to do this from settings department or employee and the admin has
 * full right add website in folder this folder has name which will be
 * domain/foldername show the website only html and css and js"). The list of the
 * sites one looks after — every site for an administrator — and each site's own
 * page: its address, and its files folder by folder, which take files, folders
 * and .zip files dropped on them. The forms (new site, settings, delete, rename,
 * the editor) are pages of their own, in SiteForms.tsx.
 */

// ---------------------------------------------------------------------------
// The list
// ---------------------------------------------------------------------------

export function SitesPage() {
  const { t, locale, localized } = useI18n();
  const navigate = useNavigate();
  const list = useQuery({ queryKey: [...SITES_KEY, 'list'], queryFn: () => api.get<SitesList>('/sites') });
  const data = list.data;

  return (
    <>
      <PageHeader
        title={t('sites.title')}
        count={data?.sites.length}
        subtitle={
          data ? (
            <>
              {t('sites.subtitle')}{' '}
              {/* An address reads left to right in Arabic too: on its own, the sentence's direction would scramble it. */}
              <span dir="ltr" className="inline-block break-all">
                {data.address}/…/
              </span>
            </>
          ) : undefined
        }
        action={
          <>
            {data?.managesAll && (
              <Button icon={<Users className="h-4 w-4" aria-hidden />} onClick={() => navigate('/settings?tab=sites')}>
                {t('sites.whoPublishes')}
              </Button>
            )}
            <Button variant="primary" icon={<Plus className="h-4 w-4" aria-hidden />} onClick={() => navigate('/sites/new')}>
              {t('sites.new')}
            </Button>
          </>
        }
      />
      <Card padded={false}>
        {list.isLoading ? (
          <LoadingBlock />
        ) : list.error ? (
          <ErrorState error={list.error} onRetry={() => void list.refetch()} />
        ) : !data?.sites.length ? (
          <EmptyState
            icon={<AppWindow className="h-8 w-8" aria-hidden />}
            title={t('sites.empty')}
            hint={t('sites.emptyHint')}
            action={
              <Button variant="primary" icon={<Plus className="h-4 w-4" aria-hidden />} onClick={() => navigate('/sites/new')}>
                {t('sites.new')}
              </Button>
            }
          />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>{t('sites.site')}</Th>
                {data.managesAll && <Th className="hidden md:table-cell">{t('sites.owner')}</Th>}
                <Th className="hidden sm:table-cell">{t('sites.files')}</Th>
                <Th>{t('sites.status')}</Th>
                <Th className="hidden lg:table-cell">{t('sites.updated')}</Th>
              </tr>
            </thead>
            <tbody>
              {data.sites.map((site) => (
                <Tr key={site.id} onClick={() => navigate(`/sites/${site.id}`)}>
                  <Td className="max-w-[28rem]">
                    <Link
                      to={`/sites/${site.id}`}
                      onClick={(e) => e.stopPropagation()}
                      className="block truncate font-medium hover:underline"
                    >
                      {site.name}
                    </Link>
                    <a
                      href={site.address}
                      target="_blank"
                      rel="noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      dir="ltr"
                      className="inline-flex max-w-full items-center gap-1 truncate text-caption text-primary-600 hover:underline dark:text-primary-400"
                    >
                      <span className="truncate">{site.address}</span>
                      <ExternalLink className="h-3 w-3 shrink-0" aria-hidden />
                    </a>
                  </Td>
                  {data.managesAll && <Td className="hidden md:table-cell text-muted">{localized(site.owner)}</Td>}
                  <Td className="hidden sm:table-cell whitespace-nowrap text-muted tabular">
                    {t('sites.fileCount', { n: site.fileCount })} · {formatBytes(site.bytes, locale)}
                  </Td>
                  <Td>
                    <SiteStatus site={site} />
                  </Td>
                  <Td className="hidden lg:table-cell whitespace-nowrap text-muted">
                    <span title={formatDateTime(site.updatedAt, locale)}>{relativeTime(site.updatedAt, locale)}</span>
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </>
  );
}

/** Live, switched off, or up but without its front page. */
function SiteStatus({ site }: { site: SiteSummary }) {
  const { t } = useI18n();
  if (!site.isLive) return <Badge tone="warning">{t('sites.offline')}</Badge>;
  if (!site.hasIndex) return <Badge tone="neutral">{t('sites.noFrontPage')}</Badge>;
  return <Badge tone="success">{t('sites.live')}</Badge>;
}

// ---------------------------------------------------------------------------
// One site
// ---------------------------------------------------------------------------

/** A site's page: its address, and its files — the folder in `?folder=`, which may not hold anything yet. */
export function SitePage() {
  const { id = '' } = useParams();
  const [params] = useSearchParams();
  const folder = cleanPath(params.get('folder') ?? '');
  const { t, locale, localized } = useI18n();
  const navigate = useNavigate();
  const toast = useToast();
  const site = useQuery({ queryKey: [...SITES_KEY, 'site', id], queryFn: () => api.get<SiteDetails>(`/sites/${id}`) });
  const data = site.data;
  const uploads = useSiteUploads(data, folder);
  const deletes = useSiteDeletes(id);
  const download = useSiteDownload(id);
  const here = useMemo(() => (data ? listing(data.files, folder) : { folders: [], files: [] }), [data, folder]);

  if (site.isLoading) return <LoadingBlock />;
  if (site.error || !data) {
    return <ErrorState error={site.error ?? new Error(t('common.error'))} onRetry={() => void site.refetch()} />;
  }

  const copy = () =>
    navigator.clipboard
      .writeText(data.address)
      .then(() => toast.success(t('sites.copied')))
      .catch(() => toast.error(t('common.error')));
  const inFolder = (name: string) => `/sites/${id}?folder=${encodeURIComponent(folder ? `${folder}/${name}` : name)}`;

  return (
    <DropArea uploads={uploads} folder={folder || data.name}>
      <nav aria-label={t('sites.where')} className="mb-3 flex flex-wrap items-center gap-1 text-body-sm text-muted">
        <Link to="/sites" className="hover:text-[var(--text-primary)] hover:underline">
          {t('sites.title')}
        </Link>
        <ChevronRight className="h-3.5 w-3.5 flip-rtl text-faint" aria-hidden />
        <Link to={`/sites/${id}`} className="max-w-[14rem] truncate hover:text-[var(--text-primary)] hover:underline">
          {data.name}
        </Link>
        {folder.split('/').filter(Boolean).map((part, i, parts) => (
          <span key={i} className="inline-flex items-center gap-1">
            <ChevronRight className="h-3.5 w-3.5 flip-rtl text-faint" aria-hidden />
            <Link
              to={`/sites/${id}?folder=${encodeURIComponent(parts.slice(0, i + 1).join('/'))}`}
              className="max-w-[12rem] truncate hover:text-[var(--text-primary)] hover:underline"
            >
              {part}
            </Link>
          </span>
        ))}
      </nav>

      <PageHeader
        title={
          <span className="inline-flex items-center gap-2">
            <AppWindow className="h-6 w-6 shrink-0 text-[var(--text-tertiary)]" aria-hidden />
            <span className="break-all">{data.name}</span>
          </span>
        }
        subtitle={
          <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <SiteStatus site={data} />
            <a
              href={data.address}
              target="_blank"
              rel="noreferrer"
              dir="ltr"
              className="inline-flex items-center gap-1 break-all text-primary-600 hover:underline dark:text-primary-400"
            >
              {data.address}
              <ExternalLink className="h-3.5 w-3.5 shrink-0" aria-hidden />
            </a>
            <button
              type="button"
              onClick={copy}
              title={t('sites.copyAddress')}
              aria-label={t('sites.copyAddress')}
              className="rounded-sm p-1 text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"
            >
              <Copy className="h-3.5 w-3.5" aria-hidden />
            </button>
            <span className="text-faint">
              {t('sites.lookedAfterBy', { name: localized(data.owner) })} ·{' '}
              {t('sites.changed', { when: relativeTime(data.updatedAt, locale) })}
            </span>
          </span>
        }
        action={
          <>
            <a href={data.address} target="_blank" rel="noreferrer">
              <Button icon={<ExternalLink className="h-4 w-4" aria-hidden />}>{t('sites.open')}</Button>
            </a>
            <Menu
              label={t('sites.menu')}
              entries={[
                { key: 'edit', label: t('sites.settings'), icon: <Settings2 className="h-4 w-4" />, run: () => navigate(`/sites/${id}/edit`) },
                {
                  key: 'delete',
                  label: t('sites.delete'),
                  icon: <Trash2 className="h-4 w-4" />,
                  run: () => navigate(`/sites/${id}/delete`),
                  danger: true,
                },
              ]}
            />
          </>
        }
      />

      {data.description && <p className="-mt-3 mb-5 max-w-3xl text-body-sm text-muted">{data.description}</p>}

      {!data.isLive ? (
        <Notice tone="warning">{t('sites.offlineNotice')}</Notice>
      ) : (
        !data.hasIndex && <Notice tone="info">{t('sites.noIndexNotice')}</Notice>
      )}

      <Card padded={false} className="mt-4">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b hairline px-4 py-3">
          <span className="text-body-sm text-muted">
            {t('sites.summary', { n: data.fileCount, size: formatBytes(data.bytes, locale), max: data.maxFiles })}
          </span>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              icon={<Download className="h-4 w-4" aria-hidden />}
              loading={download.busy}
              disabled={!here.folders.length && !here.files.length}
              title={t(folder ? 'sites.downloadFolderHint' : 'sites.downloadSiteHint')}
              onClick={() => download.start({ folder })}
            >
              {t('sites.download')}
            </Button>
            <Menu
              label={t('sites.newMenu')}
              icon={<Plus className="h-4 w-4" aria-hidden />}
              entries={[
                {
                  key: 'file',
                  label: t('sites.newFile'),
                  icon: <FilePlus className="h-4 w-4" />,
                  run: () => navigate(`/sites/${id}/new-file${folder ? `?folder=${encodeURIComponent(folder)}` : ''}`),
                },
                {
                  key: 'folder',
                  label: t('sites.newFolder'),
                  icon: <FolderPlus className="h-4 w-4" />,
                  run: () => navigate(`/sites/${id}/new-folder${folder ? `?in=${encodeURIComponent(folder)}` : ''}`),
                },
              ]}
            />
            <Menu
              label={t('sites.upload')}
              primary
              icon={<Upload className="h-4 w-4" aria-hidden />}
              entries={[
                { key: 'files', label: t('sites.uploadFiles'), icon: <Upload className="h-4 w-4" />, run: uploads.pickFiles },
                { key: 'folder', label: t('sites.uploadFolder'), icon: <FolderUp className="h-4 w-4" />, run: uploads.pickFolder },
                { key: 'zip', label: t('sites.uploadZip'), icon: <FileArchive className="h-4 w-4" />, run: uploads.pickZip },
              ]}
            />
          </div>
        </div>

        {!here.folders.length && !here.files.length ? (
          <EmptyState
            icon={<Folder className="h-8 w-8" aria-hidden />}
            title={t(folder ? 'sites.emptyFolder' : 'sites.emptySite')}
            hint={t('sites.emptyHintDrop')}
          />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>{t('sites.name')}</Th>
                <Th className="hidden md:table-cell">{t('sites.kind')}</Th>
                <Th className="hidden sm:table-cell">{t('sites.updated')}</Th>
                <Th align="end" className="hidden sm:table-cell">
                  {t('sites.size')}
                </Th>
                <Th align="end" className="w-12">
                  <span className="sr-only">{t('sites.menu')}</span>
                </Th>
              </tr>
            </thead>
            <tbody>
              {here.folders.map((row) => (
                <FolderRow
                  key={row.path}
                  row={row}
                  to={inFolder(row.name)}
                  onDownload={() => download.start({ folder: row.path })}
                  onDelete={() => deletes.ask({ folder: row })}
                />
              ))}
              {here.files.map((file) => (
                <FileRow
                  key={file.id}
                  site={data}
                  file={file}
                  onDownload={() => download.start({ path: file.path })}
                  onDelete={() => deletes.ask({ file })}
                />
              ))}
            </tbody>
          </Table>
        )}
      </Card>
      <p className="mt-3 max-w-3xl text-caption text-faint">{t('sites.dropHint')}</p>

      <UploadTray uploads={uploads} />
      {deletes.dialog}
    </DropArea>
  );
}

function Notice({ tone, children }: { tone: 'warning' | 'info'; children: ReactNode }) {
  return (
    <Card className={clsx('mt-2', tone === 'warning' ? 'border-warning/40' : 'border-info/40')}>
      <p className={clsx('text-body-sm', tone === 'warning' ? 'text-warning-fg dark:text-warning-dark' : 'text-[var(--text-secondary)]')}>
        {children}
      </p>
    </Card>
  );
}

function FolderRow({
  row,
  to,
  onDownload,
  onDelete,
}: {
  row: SiteFolderRow;
  to: string;
  onDownload: () => void;
  onDelete: () => void;
}) {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  return (
    <Tr onClick={() => navigate(to)}>
      <Td className="max-w-[24rem]">
        <div className="flex items-center gap-2.5">
          <Folder className="h-5 w-5 shrink-0 text-warning-fg dark:text-warning-dark" aria-hidden />
          <Link to={to} onClick={(e) => e.stopPropagation()} className="block truncate font-medium hover:underline" dir="auto">
            {row.name}
          </Link>
        </div>
      </Td>
      <Td className="hidden md:table-cell text-muted">{t('sites.fileCount', { n: row.files })}</Td>
      <Td className="hidden sm:table-cell whitespace-nowrap text-muted">
        <span title={formatDateTime(row.updatedAt, locale)}>{relativeTime(row.updatedAt, locale)}</span>
      </Td>
      <Td align="end" className="hidden sm:table-cell whitespace-nowrap text-muted tabular">
        {formatBytes(row.bytes, locale)}
      </Td>
      <Td align="end" className="w-12">
        <Menu
          label={t('sites.menu')}
          entries={[
            { key: 'open', label: t('sites.openFolder'), icon: <FolderInput className="h-4 w-4" />, run: () => navigate(to) },
            { key: 'download', label: t('sites.downloadZip'), icon: <Download className="h-4 w-4" />, run: onDownload },
            { key: 'delete', label: t('sites.deleteFolder'), icon: <Trash2 className="h-4 w-4" />, run: onDelete, danger: true },
          ]}
        />
      </Td>
    </Tr>
  );
}

function FileRow({
  site,
  file,
  onDownload,
  onDelete,
}: {
  site: SiteDetails;
  file: SiteFile;
  onDownload: () => void;
  onDelete: () => void;
}) {
  const { t, locale, localized } = useI18n();
  const navigate = useNavigate();
  const name = file.path.slice(file.path.lastIndexOf('/') + 1);
  const Icon = file.text ? FileCode : iconFor(file.type);
  const address = fileAddress(site, file.path);
  const edit = () => navigate(`/sites/${site.id}/edit-file?path=${encodeURIComponent(file.path)}`);
  return (
    <Tr onClick={file.text ? edit : () => window.open(address, '_blank', 'noreferrer')}>
      <Td className="max-w-[24rem]">
        <div className="flex items-center gap-2.5">
          <Icon className="h-5 w-5 shrink-0 text-[var(--text-tertiary)]" aria-hidden />
          <div className="min-w-0">
            <span className="block truncate font-medium" dir="auto" title={file.path}>
              {name}
            </span>
            {file.uploadedBy && (
              <span className="block truncate text-caption text-faint">{t('sites.by', { name: localized(file.uploadedBy) })}</span>
            )}
          </div>
        </div>
      </Td>
      <Td className="hidden md:table-cell text-muted uppercase">{extensionOf(file.path).slice(1)}</Td>
      <Td className="hidden sm:table-cell whitespace-nowrap text-muted">
        <span title={formatDateTime(file.updatedAt, locale)}>{relativeTime(file.updatedAt, locale)}</span>
      </Td>
      <Td align="end" className="hidden sm:table-cell whitespace-nowrap text-muted tabular">
        {formatBytes(file.size, locale)}
      </Td>
      <Td align="end" className="w-12">
        <Menu
          label={t('sites.menu')}
          entries={[
            {
              key: 'open',
              label: t('sites.openFile'),
              icon: <ExternalLink className="h-4 w-4" />,
              run: () => window.open(address, '_blank', 'noreferrer'),
            },
            ...(file.text ? [{ key: 'edit', label: t('sites.editFile'), icon: <Pencil className="h-4 w-4" />, run: edit }] : []),
            { key: 'download', label: t('sites.downloadFile'), icon: <Download className="h-4 w-4" />, run: onDownload },
            {
              key: 'move',
              label: t('sites.moveFile'),
              icon: <FolderInput className="h-4 w-4" />,
              run: () => navigate(`/sites/${site.id}/files/${file.id}/move`),
            },
            { key: 'delete', label: t('sites.deleteFile'), icon: <Trash2 className="h-4 w-4" />, run: onDelete, danger: true },
          ]}
        />
      </Td>
    </Tr>
  );
}

/** Deleting a file, or a folder with everything in it — asked first. */
function useSiteDeletes(siteId: string) {
  const { t } = useI18n();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [asked, setAsked] = useState<{ file?: SiteFile; folder?: SiteFolderRow } | null>(null);
  const run = useMutation({
    mutationFn: (what: { file?: SiteFile; folder?: SiteFolderRow }) =>
      requestWithMessage(`/sites/${siteId}/files/delete`, {
        method: 'POST',
        body: what.file ? { paths: [what.file.path] } : { folders: [what.folder!.path] },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: SITES_KEY });
      toast.success(t('sites.deleted'));
      setAsked(null);
    },
    onError: (e: Error) => {
      toast.error(e.message);
      setAsked(null);
    },
  });

  const ask = useCallback((what: { file?: SiteFile; folder?: SiteFolderRow }) => setAsked(what), []);
  const dialog = asked ? (
    <ConfirmDialog
      title={
        asked.file
          ? t('sites.confirmDeleteFile', { name: asked.file.path })
          : t('sites.confirmDeleteFolder', { name: asked.folder!.path })
      }
      confirmLabel={t('common.delete')}
      danger
      busy={run.isPending}
      onConfirm={() => run.mutate(asked)}
      onCancel={() => setAsked(null)}
    >
      <p>
        {asked.file ? t('sites.confirmDeleteFileBody') : t('sites.confirmDeleteFolderBody', { n: asked.folder!.files })}
      </p>
    </ConfirmDialog>
  ) : null;
  return { ask, dialog };
}

/**
 * Downloading the whole site or a folder of it as a .zip, or one file (operator,
 * 2026-11-03: "add download for all folder"): the API signs a link for it, and
 * the browser saves what the link sends. `busy` while the link is being made.
 */
function useSiteDownload(siteId: string) {
  const { t } = useI18n();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const start = useCallback(
    (target: DownloadTarget) => {
      setBusy(true);
      api
        .post<SiteDownload>(`/sites/${siteId}/download`, target)
        .then(saveDownload)
        .catch((e: Error) =>
          toast.error(e instanceof ApiError && e.code === 'SITE_DOWNLOAD_TOO_BIG' ? t('sites.downloadTooBig') : e.message),
        )
        .finally(() => setBusy(false));
    },
    [siteId, toast, t],
  );
  return { start, busy };
}

// ---------------------------------------------------------------------------
// A menu: a row's ⋯, or a labelled button
// ---------------------------------------------------------------------------

interface MenuEntry {
  key: string;
  label: string;
  icon: ReactNode;
  run: () => void;
  danger?: boolean;
}

/**
 * Its entries drawn over the page (in a portal), under its button's end edge —
 * right in English, left in Arabic — or above it near the bottom of the window.
 * With an `icon` it is a labelled button; without one, a row's ⋯.
 */
function Menu({
  label,
  entries,
  icon,
  primary = false,
}: {
  label: string;
  entries: MenuEntry[];
  icon?: ReactNode;
  primary?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [at, setAt] = useState<{ top: number; left: number } | null>(null);
  const anchor = useRef<HTMLSpanElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  const close = useCallback((focus = false) => {
    setOpen(false);
    if (focus) anchor.current?.querySelector('button')?.focus();
  }, []);
  const toggle = (e: { stopPropagation: () => void }) => {
    e.stopPropagation();
    setAt(null);
    setOpen((was) => !was);
  };

  useLayoutEffect(() => {
    if (!open || !anchor.current || !menu.current) return;
    const rect = anchor.current.getBoundingClientRect();
    const rtl = getComputedStyle(anchor.current).direction === 'rtl';
    const width = menu.current.offsetWidth;
    const height = menu.current.offsetHeight;
    const up = rect.bottom + height + 12 > window.innerHeight && rect.top - height - 12 > 0;
    const left = rtl ? Math.min(window.innerWidth - width - 8, rect.left) : Math.max(8, rect.right - width);
    setAt({ top: up ? rect.top - height - 4 : rect.bottom + 4, left });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!menu.current?.contains(e.target as Node) && !anchor.current?.contains(e.target as Node)) close();
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && close(true);
    const onMove = () => close();
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', onMove);
    document.addEventListener('scroll', onMove, true);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onMove);
      document.removeEventListener('scroll', onMove, true);
    };
  }, [open, close]);

  return (
    <>
      <span ref={anchor} className="inline-flex">
        {icon ? (
          <Button variant={primary ? 'primary' : 'secondary'} icon={icon} aria-haspopup="menu" aria-expanded={open} onClick={toggle}>
            {label}
          </Button>
        ) : (
          <button
            type="button"
            aria-haspopup="menu"
            aria-expanded={open}
            aria-label={label}
            title={label}
            onClick={toggle}
            className="inline-flex h-8 w-8 items-center justify-center rounded-sm text-[var(--text-secondary)] hover:bg-[var(--surface-sunken)] hover:text-[var(--text-primary)]"
          >
            <MoreHorizontal className="h-4 w-4" aria-hidden />
          </button>
        )}
      </span>
      {open &&
        createPortal(
          <div
            ref={menu}
            role="menu"
            onClick={(e) => e.stopPropagation()}
            style={at ? { top: at.top, left: at.left } : { top: -9999, left: -9999 }}
            className="fixed z-dropdown min-w-[12rem] rounded-md surface-raised py-1 shadow-lg animate-boot-in"
          >
            {entries.map((entry) => (
              <button
                key={entry.key}
                type="button"
                role="menuitem"
                onClick={() => {
                  close();
                  entry.run();
                }}
                className={clsx(
                  'flex w-full items-center gap-2.5 px-3 py-2 text-start text-body-sm hover:bg-[var(--surface-sunken)]',
                  entry.danger ? 'text-error-fg dark:text-error-dark' : 'text-[var(--text-primary)]',
                )}
              >
                <span aria-hidden>{entry.icon}</span>
                {entry.label}
              </button>
            ))}
          </div>,
          document.body,
        )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Uploading: picked, or dropped on the page
// ---------------------------------------------------------------------------

interface SiteUpload {
  key: string;
  path: string;
  size: number;
  progress: number;
  status: 'waiting' | 'uploading' | 'done' | 'error' | 'skipped';
  /** A file was at that path already: it is the new one now. */
  replaces: boolean;
  error?: string;
}

/** At most this many go up at once; the rest wait their turn. */
const AT_ONCE = 4;

const SKIPPED: Record<SkipReason, TranslationKey> = {
  HIDDEN: 'sites.skipHidden',
  TYPE: 'sites.skipType',
  NAME: 'sites.skipName',
};

const ZIP_PROBLEM: Record<ZipProblem, TranslationKey> = {
  NOT_ZIP: 'sites.zipNotZip',
  TOO_BIG: 'sites.zipTooBig',
  ENCRYPTED: 'sites.zipEncrypted',
  METHOD: 'sites.zipMethod',
  BROKEN: 'sites.zipBroken',
  UNSUPPORTED: 'sites.zipUnsupported',
};

/**
 * Files put in a folder of the site, one upload each straight to the file
 * storage, a few at a time — each at its path, replacing any file there. What a
 * site would refuse is shown as skipped, never sent; the site's page refreshes
 * once the last has gone up.
 */
function useSiteUploads(site: SiteDetails | undefined, folder: string) {
  const { t, locale } = useI18n();
  const toast = useToast();
  const queryClient = useQueryClient();
  const policy = useFilePolicy();
  const [items, setItems] = useState<SiteUpload[]>([]);
  const [dragging, setDragging] = useState(false);
  const [reading, setReading] = useState(false);
  const queue = useRef<Array<{ key: string; path: string; blob: () => Promise<Blob> }>>([]);
  const running = useRef(0);
  const depth = useRef(0);
  const inputs = useRef<{ files?: HTMLInputElement; folder?: HTMLInputElement; zip?: HTMLInputElement }>({});
  const siteId = site?.id;

  const patch = (key: string, change: Partial<SiteUpload>) =>
    setItems((list) => list.map((u) => (u.key === key ? { ...u, ...change } : u)));

  const failure = useCallback(
    (e: unknown): string => {
      if (e instanceof ApiError) {
        if (e.code === 'FILE_TOO_LARGE') return t('sites.tooLarge', { mb: String((e.details as { maxMb?: number })?.maxMb ?? '') });
        if (e.code === 'FILE_STORAGE_UNREACHABLE' || e.code === 'FILE_STORAGE_UNAVAILABLE') return t('drive.storageUnreachable');
        return e.message;
      }
      return t('sites.uploadFailed');
    },
    [t],
  );

  const pump = useCallback(() => {
    if (!siteId) return;
    while (running.current < AT_ONCE && queue.current.length) {
      const next = queue.current.shift()!;
      running.current++;
      patch(next.key, { status: 'uploading' });
      next
        .blob()
        .then((blob) =>
          uploadToSite<SiteFile>(blob, siteId, next.path, { onProgress: (fraction) => patch(next.key, { progress: fraction }) }),
        )
        .then(() => patch(next.key, { status: 'done', progress: 1 }))
        .catch((e) => patch(next.key, { status: 'error', error: failure(e) }))
        .finally(() => {
          running.current--;
          // The list once the last is up: a site of hundreds of files is not read again for each.
          if (running.current === 0 && queue.current.length === 0) void queryClient.invalidateQueries({ queryKey: SITES_KEY });
          pump();
        });
    }
  }, [siteId, queryClient, failure]);

  const add = useCallback(
    (outgoing: Outgoing[]) => {
      if (!site || !outgoing.length) return;
      const there = new Set(site.files.map((f) => f.path));
      const fresh = outgoing.map((o) => {
        const path = cleanPath(folder ? `${folder}/${o.path}` : o.path);
        const reason = skipReason(path);
        const maxMb = isMedia(path) ? (policy?.maxVideoMb ?? policy?.maxUploadMb) : policy?.maxUploadMb;
        const tooBig = !reason && maxMb !== undefined && o.size > maxMb * 1024 * 1024;
        const item: SiteUpload = {
          key: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
          path,
          size: o.size,
          progress: 0,
          status: reason ? 'skipped' : tooBig ? 'error' : 'waiting',
          replaces: there.has(path),
          error: reason ? t(SKIPPED[reason]) : tooBig ? t('sites.tooLarge', { mb: String(maxMb) }) : undefined,
        };
        return { item, blob: o.blob };
      });
      setItems((list) => [...list.filter((u) => u.status === 'waiting' || u.status === 'uploading'), ...fresh.map((f) => f.item)]);
      queue.current.push(...fresh.filter((f) => f.item.status === 'waiting').map((f) => ({ key: f.item.key, path: f.item.path, blob: f.blob })));
      pump();
    },
    [site, folder, policy, pump, t],
  );

  /** A folder or a .zip is read before anything goes up; what cannot be read says why. */
  const take = useCallback(
    async (read: () => Promise<Outgoing[]>) => {
      setReading(true);
      try {
        add(await read());
      } catch (e) {
        toast.error(e instanceof ZipError ? t(ZIP_PROBLEM[e.problem]) : t('sites.readFailed'));
      } finally {
        setReading(false);
      }
    },
    [add, toast, t],
  );

  // One file input for each way of picking: files, a folder, a .zip.
  useEffect(() => {
    const make = (setup: (el: HTMLInputElement) => void, onPick: (files: File[]) => void) => {
      const el = document.createElement('input');
      el.type = 'file';
      setup(el);
      el.onchange = () => {
        onPick(Array.from(el.files ?? []));
        el.value = '';
      };
      return el;
    };
    inputs.current = {
      files: make((el) => (el.multiple = true), (files) => add(pickedFiles(files))),
      folder: make(
        (el) => {
          el.multiple = true;
          el.setAttribute('webkitdirectory', '');
        },
        (files) => add(pickedFolder(files)),
      ),
      zip: make(
        (el) => (el.accept = '.zip,application/zip'),
        (files) => files[0] && void take(() => zipFiles(files[0])),
      ),
    };
    return () => {
      inputs.current = {};
    };
  }, [add, take]);

  const drag = {
    onDragEnter: (e: DragEvent) => {
      if (!site || !Array.from(e.dataTransfer.types).includes('Files')) return;
      e.preventDefault();
      depth.current++;
      setDragging(true);
    },
    onDragOver: (e: DragEvent) => {
      if (site && Array.from(e.dataTransfer.types).includes('Files')) e.preventDefault();
    },
    onDragLeave: () => {
      if (!site) return;
      depth.current = Math.max(0, depth.current - 1);
      if (depth.current === 0) setDragging(false);
    },
    onDrop: (e: DragEvent) => {
      if (!site) return;
      e.preventDefault();
      depth.current = 0;
      setDragging(false);
      // Read now: the browser takes the dropped entries away once the event is over.
      const reading = droppedFiles(e.dataTransfer);
      void take(() => reading);
    },
  };

  return {
    items,
    dragging,
    reading,
    drag,
    pickFiles: () => inputs.current.files?.click(),
    pickFolder: () => inputs.current.folder?.click(),
    pickZip: () => inputs.current.zip?.click(),
    dismiss: () => setItems((list) => list.filter((u) => u.status === 'waiting' || u.status === 'uploading')),
    sizeLabel: (bytes: number) => formatBytes(bytes, locale),
  };
}

type Uploads = ReturnType<typeof useSiteUploads>;

/** The page, which takes files, folders and .zip files dropped on it — with a veil saying where they go. */
function DropArea({ uploads, folder, children }: { uploads: Uploads; folder: string; children: ReactNode }) {
  const { t } = useI18n();
  return (
    <div {...uploads.drag} className="relative min-h-full">
      {children}
      {(uploads.dragging || uploads.reading) && (
        <div className="pointer-events-none fixed inset-0 z-overlay grid place-items-center bg-primary/10 backdrop-blur-[1px]">
          <div className="rounded-lg border-2 border-dashed border-primary bg-[var(--surface-raised)] px-8 py-6 text-center shadow-lg">
            <Upload className="mx-auto mb-2 h-8 w-8 text-primary" aria-hidden />
            <p className="text-title text-[var(--text-primary)]">
              {uploads.reading ? t('sites.reading') : t('sites.dropHere', { folder })}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

/** What is going up, what was skipped and what went wrong — at the foot of the screen until put away. */
function UploadTray({ uploads }: { uploads: Uploads }) {
  const { t } = useI18n();
  if (!uploads.items.length) return null;
  const busy = uploads.items.filter((u) => u.status === 'waiting' || u.status === 'uploading').length;
  const done = uploads.items.filter((u) => u.status === 'done').length;
  const skipped = uploads.items.filter((u) => u.status === 'skipped').length;
  const failed = uploads.items.filter((u) => u.status === 'error').length;
  return (
    <div className="fixed bottom-4 end-4 z-sticky w-[min(24rem,calc(100vw-2rem))] rounded-lg surface-raised shadow-lg">
      <div className="flex items-center justify-between gap-2 border-b hairline px-4 py-2.5">
        <span className="text-body-sm font-medium text-[var(--text-primary)]">
          {busy ? t('sites.uploading', { n: busy }) : t('sites.uploadsDone', { n: done })}
          {(skipped > 0 || failed > 0) && (
            <span className="ms-2 font-normal text-muted">
              {[skipped ? t('sites.skippedCount', { n: skipped }) : '', failed ? t('sites.failedCount', { n: failed }) : '']
                .filter(Boolean)
                .join(' · ')}
            </span>
          )}
        </span>
        {!busy && (
          <button
            type="button"
            onClick={uploads.dismiss}
            aria-label={t('drive.dismiss')}
            className="rounded-sm p-1 text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        )}
      </div>
      <ul className="max-h-72 space-y-2.5 overflow-y-auto px-4 py-3">
        {uploads.items.map((u) => (
          <li key={u.key} className="text-caption">
            <div className="flex items-center justify-between gap-2">
              <span className="truncate text-[var(--text-primary)]" dir="ltr" title={u.path}>
                {u.path}
              </span>
              <span
                className={clsx(
                  'shrink-0',
                  u.status === 'error'
                    ? 'text-error-fg dark:text-error-dark'
                    : u.status === 'done'
                      ? 'text-success-fg dark:text-success'
                      : 'text-muted',
                )}
              >
                {u.status === 'done'
                  ? t(u.replaces ? 'sites.replaced' : 'sites.uploaded')
                  : u.status === 'error'
                    ? t('sites.uploadFailed')
                    : u.status === 'skipped'
                      ? t('sites.skipped')
                      : u.status === 'waiting'
                        ? t('drive.waiting')
                        : `${Math.round(u.progress * 100)}%`}
              </span>
            </div>
            {u.status === 'uploading' && (
              <div className="mt-1">
                <ProgressBar percent={u.progress * 100} />
              </div>
            )}
            {u.error && (
              <p className={clsx('mt-0.5', u.status === 'skipped' ? 'text-faint' : 'text-error-fg dark:text-error-dark')}>{u.error}</p>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
